import { getGableProfile, clipGableEndFace } from "building-generation/elements/roofs/roofGableEnds";
import {
  invertRoofHeelHeightSideOffsets,
  invertGableRoofType,
  makeRoofHeelHeightOffsets,
} from "building-generation/elements/roofs/roofHeelHeightOffsets";
import { getFirstSlope } from "building-generation/elements/roofs/roofSlopes";
import { ROOF_WALL_FACE_TAG } from "building-types/roofs/generatedRoofSystemTypes";
import type {
  GenerateRoofVolumeOptions,
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { RoofGableFaces } from "building-types/settings/roofTypes";
import { assertIsNotNullOrUndefined, assertIsNumber } from "common/utils/assertOfType";
import { GABLE_END_FACE_OFFSET, roofGableFacesNew } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { faceOrNullMoveAlongNormal, faceNew, tagFace } from "geom/brep";
import { planeByThreePoints, planeNormal, planeNew, planeCopy } from "geom/plane";
import { polyLine2End, polyLine2Start } from "geom/polyLine2";
import { vec3Cross } from "geom/vec3";
import { getEaveDepth } from "../../../elements/roofTrimUtility";

/**
 * Generates the gable end faces of the roof if needed
 */
export function generateRoofVolumeGableFaces({
  roofVolumeOptions,
  roofVolume,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const startFaces = buildGableFaces(roofVolumeOptions, roofVolume, false);
  const endFaces = buildGableFaces(roofVolumeOptions, roofVolume, true);

  return {
    roofVolume: {
      startFaces,
      endFaces,
    },
  };
}

function buildGableFaces(
  roofVolumeOptions: GenerateRoofVolumeOptions,
  roofVolume: Partial<GeneratedRoofVolumeLegacy>,
  isEnd: boolean,
): RoofGableFaces {
  const {
    baseHeight,
    span,
    rails2D,
    peakHeight,
    verticalRails,
    shedRoofHalfFace,
    shedRoofWallHalfFace,
    extrusion,
    rectangularOverhangs,
    startConnectivity,
    endConnectivity,
    rectangularExtWallOffsetWithFinish,
  } = roofVolume;

  assertIsNotNullOrUndefined(rails2D);
  const { peakRailNoExtension } = rails2D;

  const { roofInputSettings, roofData, startEdge, endEdge } = roofVolumeOptions;
  const { roofSettings } = roofInputSettings;
  const { suppressWallExtension } = roofData;

  assertIsNotNullOrUndefined(extrusion);
  const endFace = isEnd ? extrusion.endFace : extrusion.startFace;
  const connectivity = isEnd ? endConnectivity : startConnectivity;

  assertIsNotNullOrUndefined(connectivity);
  const { isFree } = connectivity;

  // early return for gable ends connected to other roofs
  if (!endFace || !isFree) {
    return roofGableFacesNew();
  }

  const edgeIndex = isEnd ? endEdge : startEdge;

  assertIsNotNullOrUndefined(rectangularExtWallOffsetWithFinish);
  const edgeCount = rectangularExtWallOffsetWithFinish.length;
  const exteriorWallOffsetWithFinish = rectangularExtWallOffsetWithFinish[edgeIndex];
  const prevExteriorWallOffsetWithFinish = rectangularExtWallOffsetWithFinish[(edgeIndex + edgeCount - 1) % edgeCount];
  const nextExteriorWallOffsetWithFinish = rectangularExtWallOffsetWithFinish[(edgeIndex + 1) % edgeCount];

  assertIsNotNullOrUndefined(verticalRails);

  // prerare input data
  const {
    isStartTruncated,
    isStartFullyTruncated,
    truncatedStartHeight,
    truncatedStartHeight2,
    isEndTruncated,
    isEndFullyTruncated,
    truncatedEndHeight,
    truncatedEndHeight2,
  } = verticalRails;

  const isTruncated = isEnd ? isEndTruncated : isStartTruncated;
  const isFullyTruncated = isEnd ? isEndFullyTruncated : isStartFullyTruncated;
  const truncatedHeight = isEnd ? truncatedEndHeight : truncatedStartHeight;
  const truncatedHeight2 = isEnd ? truncatedEndHeight2 : truncatedStartHeight2;
  let heelHeightOffsets;
  if (isEnd) {
    assertIsNotNullOrUndefined(roofVolume?.heelHeightOffsets);
    heelHeightOffsets = invertRoofHeelHeightSideOffsets(roofVolume.heelHeightOffsets);
  } else {
    heelHeightOffsets = roofVolume.heelHeightOffsets;
  }
  let roofType;
  if (isEnd) {
    assertIsNotNullOrUndefined(roofVolume?.roofType);
    roofType = invertGableRoofType(roofVolume.roofType);
  } else {
    roofType = roofVolume.roofType;
  }

  assertIsNotNullOrUndefined(rectangularOverhangs);
  const roofOverhang = isEnd ? rectangularOverhangs.endOverhang : rectangularOverhangs.startOverhang;

  const leftSpan = isEnd ? roofVolume.rightSpan : roofVolume.leftSpan;
  const rightSpan = isEnd ? roofVolume.leftSpan : roofVolume.rightSpan;

  const leftRail = isEnd ? rails2D.rightRail : rails2D.leftRail;
  const rightRail = isEnd ? rails2D.leftRail : rails2D.rightRail;
  const leftSideSlopes = isEnd ? roofVolume.rightSideSlopes : roofVolume.leftSideSlopes;
  const rightSideSlopes = isEnd ? roofVolume.leftSideSlopes : roofVolume.rightSideSlopes;
  let leftWallToRoofEdgeDistance = isEnd ? rectangularOverhangs.rightOverhang : rectangularOverhangs.leftOverhang;
  let rightWallToRoofEdgeDistance = isEnd ? rectangularOverhangs.leftOverhang : rectangularOverhangs.rightOverhang;

  leftWallToRoofEdgeDistance -= prevExteriorWallOffsetWithFinish;
  rightWallToRoofEdgeDistance -= nextExteriorWallOffsetWithFinish;

  assertIsNotNullOrUndefined(leftSideSlopes);
  const { slope: firstSlopeLeft } = getFirstSlope(leftSideSlopes);

  assertIsNotNullOrUndefined(rightSideSlopes);
  const { slope: firstSlopeRight } = getFirstSlope(rightSideSlopes);
  const wallExtensionHeightLeft = firstSlopeLeft * leftWallToRoofEdgeDistance;
  const wallExtensionHeightRight = firstSlopeRight * rightWallToRoofEdgeDistance;

  assertIsNumber(span);

  // build the different gable profiles
  const gableProfile = getGableProfile({
    leftSideSlopes,
    rightSideSlopes,
    span,
    leftWallToRoofEdgeDistance,
    rightWallToRoofEdgeDistance,
    wallExtensionHeightLeft,
    wallExtensionHeightRight,
    truncatedHeight: truncatedHeight2,
    truncated: isTruncated,
    closed: true,
    roofType,
    leftSpan,
    rightSpan,
    topOffset: GABLE_END_FACE_OFFSET,
    heelHeightOffsets: suppressWallExtension
      ? makeRoofHeelHeightOffsets(getEaveDepth(roofSettings))
      : heelHeightOffsets,
  });

  const discardGableProfile = getGableProfile({
    leftSideSlopes,
    rightSideSlopes,
    span,
    leftWallToRoofEdgeDistance: 0,
    rightWallToRoofEdgeDistance: 0,
    wallExtensionHeightLeft: 0,
    wallExtensionHeightRight: 0,
    truncatedHeight,
    truncated: isTruncated,
    closed: true,
    roofType,
    leftSpan,
    rightSpan,
    topOffset: GABLE_END_FACE_OFFSET,
    heelHeightOffsets,
  });

  // build main plane
  const [x0, y0] = isEnd ? polyLine2End(leftRail) : polyLine2Start(leftRail);
  const [x1, y1] = isEnd ? polyLine2End(rightRail) : polyLine2Start(rightRail);
  const [x2, y2] = isEnd ? polyLine2End(peakRailNoExtension) : polyLine2Start(peakRailNoExtension);

  assertIsNumber(baseHeight);
  assertIsNumber(peakHeight);
  const srcPlane = planeByThreePoints(x0, y0, baseHeight, x1, y1, baseHeight, x2, y2, peakHeight);

  if (!srcPlane) {
    return roofGableFacesNew();
  }

  const [nX, nY, nZ] = planeNormal(srcPlane);
  const { originX, originY } = srcPlane;
  const [uX, uY, uZ] = vec3Cross(0, 0, 1, nX, nY, nZ);
  const plane = planeNew(originX, originY, baseHeight, uX, uY, uZ, 0, 0, 1);

  let frontFace: Face | null = null;
  let discardFrontFace: Face | null = null;

  // build the faces if this end is not fully truncated
  if (!isFullyTruncated) {
    frontFace = faceNew([gableProfile], planeCopy(plane));
    faceOrNullMoveAlongNormal(frontFace, -roofOverhang + exteriorWallOffsetWithFinish);

    discardFrontFace = faceNew([discardGableProfile], planeCopy(plane));

    frontFace = clipGableEndFace(frontFace, shedRoofWallHalfFace ?? null);
    frontFace && tagFace(frontFace, ROOF_WALL_FACE_TAG);
    discardFrontFace = clipGableEndFace(discardFrontFace, shedRoofHalfFace ?? null);
  }

  return roofGableFacesNew(plane, frontFace, discardFrontFace);
}
